/**
 * Theme Store
 * ───────────
 * Three modes:
 *   'system'  → follow OS prefers-color-scheme (default)
 *   'light'   → always light, remove 'dark' from <html>
 *   'dark'    → always dark,  add    'dark' to <html>
 *
 * Persisted to localStorage under key 'hedui-theme'.
 * Syncs to DOM immediately on every change.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'system' | 'light' | 'dark';

interface ThemeState {
    mode: ThemeMode;
    /** Whether the effective theme is currently dark */
    isDark: boolean;
    setMode: (mode: ThemeMode) => void;
}

/** Apply 'dark' class to <html> element based on mode */
function applyTheme(mode: ThemeMode): boolean {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const shouldBeDark =
        mode === 'dark' || (mode === 'system' && prefersDark);

    if (shouldBeDark) {
        document.documentElement.classList.add('dark');
    } else {
        document.documentElement.classList.remove('dark');
    }
    return shouldBeDark;
}

export const useThemeStore = create<ThemeState>()(
    persist(
        (set, get) => ({
            mode: 'system',
            isDark: applyTheme('system'),

            setMode: (mode) => {
                const isDark = applyTheme(mode);
                set({ mode, isDark });
            },
        }),
        {
            name: 'hedui-theme',
            // Re-apply theme on hydration (store restored from localStorage)
            onRehydrateStorage: () => (state) => {
                if (state) {
                    const isDark = applyTheme(state.mode);
                    state.isDark = isDark;
                }
            },
        },
    ),
);

/**
 * Call this once at app startup to:
 *  1. Apply the persisted theme before React renders (prevents flash)
 *  2. Listen for OS theme changes when mode==='system'
 */
export function initTheme() {
    // Read persisted value directly (before store hydrates)
    try {
        const raw = localStorage.getItem('hedui-theme');
        if (raw) {
            const { state } = JSON.parse(raw);
            if (state?.mode) applyTheme(state.mode);
        } else {
            applyTheme('system');
        }
    } catch {
        applyTheme('system');
    }

    // Keep 'system' mode in sync with OS changes
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', () => {
        const store = useThemeStore.getState();
        if (store.mode === 'system') {
            const isDark = applyTheme('system');
            useThemeStore.setState({ isDark });
        }
    });
}
